import React from "react";
import { IDisplayAttachmentsProps } from "./DisplayAttachments.types";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { CSS_CONSTANTS } from "../../../../constants/cssConstants";
import {
  base64ToBlob,
  formatFileSize,
  getExtension,
  isImageAttachment,
} from "../../../../helpers";
import { getAttachmentIcon } from "../OutlookEmailContent/getAttachmentIcon";
import { DownloadOutlined, VisibilityOutlined } from "@mui/icons-material";
import { GraphAttachment } from "../../../../redux/outlook/outlook.types";
import { IPreviewAttachment } from "../PreviewAttachmentDialog/PreviewAttachmentDialog.types";
import { PreviewAttachmentDialog } from "../PreviewAttachmentDialog/PreviewAttachmentDialog";

export const DisplayAttachments: React.FC<IDisplayAttachmentsProps> = ({
  attachments,
}) => {
  const [preview, setPreview] = React.useState<IPreviewAttachment | null>(null);

    const handleClosePreview = () => {
    if (preview?.url) {
      URL.revokeObjectURL(preview.url);
    }
    setPreview(null);
  };


  const createObjectUrl = (attachment: GraphAttachment): string | null => {
    if (!attachment.contentBytes) {
      return null;
    }

    const blob = base64ToBlob(
      attachment.contentBytes,
      attachment.contentType ||
        attachment["@odata.mediaContentType"] ||
        "application/octet-stream",
    );

    return URL.createObjectURL(blob);
  };

  const handlePreviewAttachment = (attachment: GraphAttachment) => {
    const objectUrl = createObjectUrl(attachment);

    if (!objectUrl) {
      return;
    }

    setPreview({
      url: objectUrl,
      name: attachment.name,
      contentType: attachment.contentType || "application/octet-stream",
    });
  };

  const handleDownloadAttachment = (attachment: GraphAttachment) => {
    const objectUrl = createObjectUrl(attachment);

    if (!objectUrl) {
      return;
    }

    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = attachment.name || "attachment";
    anchor.click();

    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1000);
  };

  return (
    <>
      {attachments.length > 0 && (
        <Box>
          <Typography
            variant="body1"
            fontWeight={500}
            sx={{ mb: 1, fontSize: CSS_CONSTANTS.LABEL_HEADING_SIZE }}
          >
            Attachments ({attachments.length})
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 1.5,
            }}
          >
            {attachments.map((attachment) => (
              <Box
                key={attachment.id}
                sx={{
                  border: "1px solid #dee3e9",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  cursor: attachment.contentBytes ? "pointer" : "default",
                  backgroundColor: "#fff",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                  },
                }}
                onClick={() => {
                  if (attachment.contentBytes) {
                    handlePreviewAttachment(attachment);
                  }
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: 140,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f7f9fc",
                  }}
                >
                  {isImageAttachment(attachment) && attachment.contentBytes ? (
                    <Box
                      component="img"
                      src={`data:${attachment.contentType || "image/*"};base64,${attachment.contentBytes}`}
                      alt={attachment.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Box sx={{ fontSize: 34, lineHeight: 1 }}>
                        {getAttachmentIcon(attachment)}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {getExtension(attachment.name) || "file"}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box minWidth={0}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      title={attachment.name}
                    >
                      {attachment.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(attachment.size)}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    px: 1,
                    pb: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                  }}
                >
                  <Tooltip title="Preview">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          handlePreviewAttachment(attachment);
                        }}
                        disabled={!attachment.contentBytes}
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Download">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDownloadAttachment(attachment);
                        }}
                        disabled={!attachment.contentBytes}
                      >
                        <DownloadOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {preview && (
        <PreviewAttachmentDialog
          open={Boolean(preview)}
          preview={preview}
          onClose={handleClosePreview}
        />
      )}
    </>
  );
};
